import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkFillerContentForm = () => {
  const { token } = useAuth();
  const [bulkText, setBulkText] = useState('');
  const [contentType, setContentType] = useState('philosophy_quotes');
  const [defaultAuthor, setDefaultAuthor] = useState('');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const contentTypes = [
    { value: 'philosophy_quotes', label: '📜 Philosophy Quotes' },
    { value: 'mindfulness_tips', label: '🧘 Mindfulness Tips' },
    { value: 'science_facts', label: '🔬 Science Facts' },
    { value: 'wellness_wisdom', label: '🌿 Wellness Wisdom' },
    { value: 'daily_motivation', label: '⭐ Daily Motivation' },
    { value: 'consciousness_facts', label: '🧠 Consciousness Facts' },
    { value: 'spiritual_wisdom', label: '✨ Spiritual Wisdom' },
    { value: 'eco_insights', label: '🌍 Eco Insights' },
    { value: 'personal_growth', label: '🌱 Personal Growth' },
    { value: 'word_power', label: '📚 Word Power' }
  ];

  const parseTextToFillerContent = (text) => {
    // Split by double line breaks to separate items
    const items = text.split('\n\n').filter(item => item.trim());
    
    return items.map(item => {
      const lines = item.trim().split('\n');
      let title = '';
      let content = '';
      let author = defaultAuthor;
      
      // Try to detect patterns
      if (lines.length === 1) {
        // Single line - use as content, generate title
        content = lines[0];
        title = content.substring(0, 30) + (content.length > 30 ? '...' : '');
      } else if (lines.length >= 2) {
        // Multiple lines - first line might be title
        if (lines[0].length < 100) {
          title = lines[0];
          content = lines.slice(1).join(' ');
        } else {
          // First line is too long to be title
          content = lines.join(' ');
          title = content.substring(0, 40) + (content.length > 40 ? '...' : '');
        }
      }
      
      // Look for author in content (after dash or by)
      const authorMatch = content.match(/ - (.+)$/) || content.match(/ by (.+)$/i);
      if (authorMatch) {
        author = authorMatch[1];
        content = content.replace(authorMatch[0], '');
      }
      
      return {
        content_type: contentType,
        title: title.replace(/^["']|["']$/g, ''), // Remove quotes
        content: content.replace(/^["']|["']$/g, '').trim(),
        size_hint: 'standard',
        category: contentType.replace('_', ' '),
        author: author || 'Modern Human Being Team'
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bulkText.trim()) {
      alert('Please paste some content first');
      return;
    }

    setProcessing(true);
    setResults(null);

    try {
      const fillerContent = parseTextToFillerContent(bulkText);
      
      const response = await axios.post(`${API}/admin/filler-content/bulk-import`, {
        filler_content: fillerContent,
        skip_duplicates: true,
        import_notes: `Bulk import of ${contentType} via admin form`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResults(response.data);
      setBulkText(''); // Clear form on success
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">📄 Bulk Add Filler Content (Copy & Paste)</h3>
      
      <div className="space-y-4">
        {/* Content Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
          <select
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {contentTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {/* Default Author */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Author (optional)</label>
          <input
            type="text"
            value={defaultAuthor}
            onChange={(e) => setDefaultAuthor(e.target.value)}
            placeholder="e.g., Ancient Wisdom, Modern Science, etc."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        {/* Bulk Text Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Copy & Paste Your Content
          </label>
          <div className="text-xs text-gray-600 mb-2">
            💡 <strong>Tip:</strong> Separate each item with double line breaks (Enter twice). 
            First line = title, rest = content. Add " - Author Name" at end for attribution.
          </div>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={`Example:

Mindful Breathing
Take three deep breaths and notice how your body relaxes with each exhale. - Jon Kabat-Zinn

Present Moment Awareness  
The present moment is the only time over which we have dominion. - Thích Nhất Hạnh

Walking Meditation
Each step can be a meditation when you pay attention to the sensation of your feet touching the ground.`}
            rows={12}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={processing || !bulkText.trim()}
          className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
            processing || !bulkText.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-emerald-600 text-white hover:bg-emerald-700'
          }`}
        >
          {processing ? '⏳ Processing...' : '📤 Import Filler Content'}
        </button>

        {/* Results Display */}
        {results && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">✅ Import Complete!</h4>
            <div className="text-sm text-green-700 space-y-1">
              <div>• Successfully imported: <strong>{results.imported_count}</strong> items</div>
              <div>• Skipped (duplicates): <strong>{results.skipped_count}</strong> items</div>
              <div>• Total processed: <strong>{results.total_processed}</strong> items</div>
            </div>
            {results.skipped_items.length > 0 && (
              <div className="mt-3 text-xs text-green-600">
                <strong>Skipped items:</strong> {results.skipped_items.map(item => item.title).join(', ')}
              </div>
            )}
            <div className="mt-2 text-xs text-green-600">
              💫 Your new content will appear in the main feed automatically!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkFillerContentForm;